import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/Auth";
import { resolveLogo, type BrandingLogos } from "@/lib/branding";

/**
 * Fetches hierarchical branding logos for the current user via RPC.
 * Returns the resolved logo URL string.
 */
export function useBrandingLogo(): { logoUrl: string; loading: boolean } {
  const { session } = useAuth();
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session?.user?.id) {
      setLogoUrl(resolveLogo({}));
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        // Use RPC instead of nested PostgREST joins to avoid schema-drift issues
        const { data, error } = await (supabase.rpc as any)("get_user_branding_context");

        if (error) {
          console.warn("[useBrandingLogo] RPC error:", error);
          if (!cancelled) {
            setLogoUrl(resolveLogo({}));
            setLoading(false);
          }
          return;
        }

        const row = Array.isArray(data) ? data[0] : data;
        const logos: BrandingLogos = {};

        if (row?.portfolio_logo_url) logos.companyLogoUrl = row.portfolio_logo_url;
        if (row?.sub_org_logo_url) logos.portfolioLogoUrl = row.sub_org_logo_url;
        if (row?.client_logo_url) logos.clientLogoUrl = row.client_logo_url;

        if (!cancelled) {
          setLogoUrl(resolveLogo(logos));
          setLoading(false);
        }
      } catch (err) {
        console.warn("[useBrandingLogo] error:", err);
        if (!cancelled) {
          setLogoUrl(resolveLogo({}));
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [session?.user?.id]);

  return { logoUrl, loading };
}
